/**
 * LoginPage — the Google Sign-In landing page.
 *
 * Flow (matches vms_database/internal/handlers/auth*.go):
 *   1. The Google button issues an ID token, or One Tap does without a click for a
 *      returning user (see promptGoogleSignIn).
 *   2. GET  /vms/api/v1/me  (Bearer: the ID token)  — returns `{ email,
 *      enrolled, orgs: [{orgId, orgName, role}], pendingInvites: [...],
 *      orgId?/orgName? (single-org compat) }`. A user may belong to many orgs.
 *      - the org this browser last used, if the caller is still an active
 *        member of it and no invitation is pending: straight back in, no
 *        picker (see decideLogin).
 *      - exactly one active org, no invites: write it as the active org,
 *        redirect.
 *      - anything else with >=1 option: show the org selector. Active orgs
 *        and pending invites appear in one list; invited entries carry an
 *        "invited" badge plus a "Cancel" action (POST /invite/decline).
 *        Picking an invited org accepts it first (POST /invite/accept),
 *        then it becomes the active org. Joining by invitation is always
 *        an explicit choice — a sole invite still shows the list.
 *      - no active org, no invite: show org-creation form (first-run).
 *      (Decision logic is the exported `decideLogin(me)` helper.)
 *   3. POST /vms/api/v1/auth/session { token, orgId } — exchanges the ID
 *      token for a cloud-minted session token of THAT organization, set
 *      as an HttpOnly cookie and returned as `{ status: "ok", token }`. The
 *      same call with the cookie alone switches the session to another org.
 *   4. POST /vms/api/v1/setup { orgName }        — creates the org and
 *      enrols the user, returns the same MeResponse shape with the new
 *      organization's session token; /invite/accept likewise.
 *
 * No screen here is a dead end: every one past the Google button names the
 * account and offers another, org creation goes Back to the chooser it came
 * from, and a later step reopens the chooser with `?choose=1`
 * (auth/loginNext.ts `getOrgChooserUrl`), whose Cancel returns to `next`.
 *
 * The session token (or the Google ID token on older backends that don't return one) is stashed in
 * sessionStorage via authSession.setToken() so that the 401 handler in api/client.ts acts only
 * when the page was signed in (`authSession.wasSignedIn`: a token, a hydrated user or the sentinel
 * cookie). The token itself is NOT forwarded as Authorization: Bearer — the API uses the
 * occurra_token HttpOnly cookie for all requests.
 */

import { Plus } from 'lucide-react';
import type { FormEvent, ReactElement } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { getApiConfig } from '@/api';
import { ApiError } from '@/api/client';
import { HTTP_STATUS } from '@/api/httpStatus';
import {
    fetchMe, postAcceptInvite, postDeclineInvite, postSession, postSetup,
} from '@/api/signIn';
import { stripOrgScopedParams } from '@/app/panelUrl';
import occurraLogo from '@/assets/occurra-logo.svg';
import { authSession } from '@/auth/authSession';
import {
    allowGoogleAutoSignIn, cancelGooglePrompt, initGoogleSignIn,
    promptGoogleSignIn, renderGoogleButton,
    signOutGoogle,
} from '@/auth/google';
import { CHOOSE_ORG_PARAM } from '@/auth/loginNext';
import { switchActiveOrg } from '@/auth/orgScope';
import { Hint } from '@/components/occurra/Hint';
import { Input } from '@/components/occurra/Input';
import { getFailureMessage } from '@/lib/failureMessage';
import { cn } from '@/lib/utils';
import type { LoginDecision, MeResponse, OrgChoice } from '@/types/auth';
import { GoogleUxMode, LoginAction } from '@/types/auth';

import { OrgChoiceRow } from './OrgChoiceRow';

/**
 * Pure decision helper — given a /me response and the org this browser was
 * last signed into, returns the next login action.
 *
 * Active orgs and pending invites merge into one choice list; invited
 * entries carry `invited: true` and render with a badge. Any pending
 * invitation holds that list open: joining an org must be an explicit user
 * action, and the list is the only place an invitation can be accepted or
 * declined, so neither shortcut below may carry the user past one.
 *
 * `rememberedOrgId` (localStorage `occurra.orgId`, written on every switch)
 * is what makes a returning multi-org user land straight back where they
 * left off instead of re-picking from the same list every single sign-in.
 * It only ever SKIPS the picker — it is honoured just when it names an org
 * the caller is still an ACTIVE member of, so a revoked membership, a
 * declined-then-forgotten org, or an id left over from another account all
 * fall back to showing the list. Switching afterwards is one click in the
 * avatar menu.
 *
 * `shouldAlwaysPick` is the chooser opened on purpose from a later step of the flow
 * (`?choose=1`), to go back and pick another organization: neither shortcut
 * applies then, however few organizations there are.
 *
 * Returns one of:
 *   { action: 'single',  orgId }    — one active org, or the remembered one
 *   { action: 'select',  choices }  — anything else with >=1 option:
 *       choices: [{orgId, orgName, role?, invitedBy?, invited: bool}]
 *   { action: 'setup' }             — zero orgs, zero invites
 *
 * Exported so it can be unit-tested in isolation without DOM / fetch mocks.
 */
export function decideLogin(
    me: MeResponse,
    savedOrgId: string | null = null,
    shouldAlwaysPick = false,
): LoginDecision {
    const choices: OrgChoice[] = [
        ...me.orgs.map((org) => ({ ...org, invited: false })),
        ...me.pendingInvites.map((invite) => ({
            orgId: invite.orgId,
            orgName: invite.orgName,
            invitedBy: invite.invitedBy,
            invitedByName: invite.invitedByName,
            invitedByPicture: invite.invitedByPicture,
            invited: true,
        })),
    ];
    if (choices.length === 0) {
        return { action: LoginAction.Setup };
    }
    // Both shortcuts stay shut while an invitation is pending: this list is
    // where the user answers one, and a shortcut past it strands them in an
    // org they already had.
    if (me.pendingInvites.length === 0 && !shouldAlwaysPick) {
        if (savedOrgId && me.orgs.some((org) => org.orgId === savedOrgId)) {
            return { action: LoginAction.Single, orgId: savedOrgId };
        }
        const onlyOrgChoice = choices.length === 1 ? choices[0] : undefined;
        if (onlyOrgChoice) {
            return { action: LoginAction.Single, orgId: onlyOrgChoice.orgId };
        }
    }
    return { action: LoginAction.Select, choices };
}

export const LoginPage = (): ReactElement => {
    const navigate = useNavigate();
    const loc = useLocation();
    const buttonRef = useRef<HTMLDivElement>(null);
    const [needsSetup, setNeedsSetup] = useState(false);
    const [orgName, setOrgName] = useState('');
    // The request under way, if any: it disables the page's controls, and their tooltips
    // name it.
    const [pendingText, setPending] = useState<string>();
    const isBusy = pendingText !== undefined;
    // Choice list from decideLogin: active orgs + pending invites (marked).
    // Kept while the org-creation form is open, which offers Back to it.
    const [orgChoices, setOrgChoices] = useState<OrgChoice[] | null>(null);
    // The account past the Google button, named beside the way to leave it.
    const [email, setEmail] = useState('');

    const params = new URLSearchParams(loc.search);
    const nextText = params.get('next') || '/';
    const isChoosing = params.get(CHOOSE_ORG_PARAM) !== null;
    const cfg = getApiConfig();

    // The chooser was opened from inside an organization this tab is signed
    // into, so cancelling it goes back there.
    const [canReturn, setCanReturn] = useState(
        (): boolean => isChoosing && !!authSession.getActiveOrg() &&
            (!!authSession.getToken() || authSession.hasSessionCookie()));

    // Inside the local-login popup (window.opener set) the Google button runs
    // in GIS REDIRECT mode: this window itself navigates to Google and back -
    // no nested Google popup (VMS-264 single-popup flow).
    const isPopupFlow = typeof window !== 'undefined' && !!window.opener;

    // Session bootstrap: any live credential — the in-tab sessionStorage token
    // or the session cookie (the popup's return leg from the Google redirect,
    // a fresh tab, or a login abandoned after the session was minted but
    // before an org was picked). Resolve /me with it and run the normal org
    // decision instead of showing the sign-in button again; a dead credential
    // is dropped and falls through to the button.
    const [isProbing, setProbing] = useState(
        (): boolean => !!authSession.getToken() || authSession.hasSessionCookie());

    // The Google ID token of this sign-in, held until an organization is
    // chosen: it is what /me and the session exchange take before the user
    // holds a session. A session token in the tab, or the cookie, serves
    // otherwise.
    const idTokenRef = useRef<string | null>(null);
    function readCredential(): string | null {
        return idTokenRef.current ?? authSession.getToken();
    }
    function hasCredential(): boolean {
        return !!readCredential() || authSession.hasSessionCookie();
    }

    // Back to the Google button, keeping nothing of the sign-in left behind.
    function goBackToSignIn(): void {
        idTokenRef.current = null;
        setOrgChoices(null);
        setNeedsSetup(false);
        setEmail('');
    }

    function onLostSession(): void {
        toast.error('Lost session — please sign in again');
        goBackToSignIn();
    }

    // Leave this account from any screen past the Google button: the server
    // cookie and the tab's token go, and Google stops offering to sign it back
    // in with one click.
    function onUseAnotherAccount(): void {
        signOutGoogle(email);
        authSession.logout();
        setCanReturn(false);
        goBackToSignIn();
    }

    // Pick an org from the choice list. An invited org is accepted first
    // (flips the membership to active server-side), then becomes the active org.
    async function onPickOrg(choice: OrgChoice): Promise<void> {
        setPending(choice.invited ? 'Joining the organization' : 'Opening the organization');
        try {
            if (!choice.invited) {
                await enterOrg(choice.orgId);
                return;
            }
            if (!hasCredential()) {
                onLostSession();
                return;
            }
            const joinedMeResponse = await postAcceptInvite(readCredential(), choice.orgId);
            goToOrg(joinedMeResponse.orgId ?? null, joinedMeResponse.token);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not join organization'));
        } finally {
            setPending(undefined);
        }
    }

    // Cancel (decline) a pending invite, then re-evaluate /me: the list
    // shrinks, collapses to a direct login, or falls back to first-run setup.
    async function onCancelInvite(choice: OrgChoice): Promise<void> {
        if (!hasCredential()) {
            onLostSession();
            return;
        }
        setPending('Declining the invitation');
        const token = readCredential();
        try {
            await postDeclineInvite(token, choice.orgId);
            const me = await fetchMe(token);
            setOrgChoices(null);
            await applyMe(me);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not cancel invitation'));
        } finally {
            setPending(undefined);
        }
    }

    async function onCredential(idToken?: string): Promise<void> {
        if (!idToken) {
            return;
        }
        setPending('Signing in');
        try {
            // The organization comes first: the session is minted for one, so the
            // ID token stays the credential until the choice is made.
            idTokenRef.current = idToken;
            await applyMe(await fetchMe(idToken));
        } catch (error) {
            toast.error(getFailureMessage(error, 'Login failed'));
        } finally {
            setPending(undefined);
        }
    }

    // Land in an org whose session this tab already holds (the exchange, the
    // setup or an accepted invite minted it) and continue to `next`.
    // switchActiveOrg drops the previous org's cached client state (panel
    // local props, chat transcript, camera caches); when it reports an actual
    // change, `next` is scrubbed of the props that name an org entity too — a
    // session that expired while viewing another org's camera comes back with
    // `?cameraId=` still on it, and that id means nothing here. The panel set
    // in `next` is kept either way: panels are app-global.
    function goToOrg(orgId: string | null, sessionToken?: string): void {
        if (sessionToken) {
            // authSession.setToken() derives the display user from the JWT claims.
            authSession.setToken(sessionToken);
            idTokenRef.current = null;
        }
        allowGoogleAutoSignIn();
        const isChanged = switchActiveOrg(orgId);
        navigate(isChanged ? stripOrgScopedParams(nextText) : nextText, { replace: true });
    }

    // Enter an org: exchange the credential for the org's session, then land.
    async function enterOrg(orgId: string | null): Promise<void> {
        if (!orgId) {
            goToOrg(null);
            return;
        }
        try {
            const session = await postSession(readCredential(), orgId);
            goToOrg(orgId, session.token);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not open organization'));
        }
    }

    // Route a decideLogin() result to the matching screen / redirect.
    async function applyDecision(decision: LoginDecision): Promise<void> {
        if (decision.action === LoginAction.Single) {
            await enterOrg(decision.orgId);
            return;
        }
        if (decision.action === LoginAction.Select) {
            setOrgChoices(decision.choices);
            return;
        }
        // Genuinely first-time user with no invite: offer org creation.
        setNeedsSetup(true);
    }

    async function applyMe(me: MeResponse): Promise<void> {
        setEmail(me.email);
        await applyDecision(decideLogin(me, authSession.getActiveOrg(), isChoosing));
    }

    async function onCreateOrg(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();
        if (!orgName.trim()) {
            return;
        }
        if (!hasCredential()) {
            onLostSession();
            return;
        }
        setPending('Creating the organization');
        try {
            const me = await postSetup(readCredential(), orgName.trim());
            goToOrg(me.orgId ?? null, me.token);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not create organization'));
        } finally {
            setPending(undefined);
        }
    }

    // Cookie-session bootstrap (see `isProbing` above).
    useEffect(() => {
        if (!isProbing) {
            return;
        }
        let isCancelled = false;
        (async () => {
            try {
                // Bearer when this tab holds the token, else cookie-authed.
                const me = await fetchMe(authSession.getToken());
                if (isCancelled) {
                    return;
                }
                await applyMe(me);
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                // Either way the sign-in button follows. Only a refused
                // credential is dead; a backend that failed to answer leaves the
                // session for when it recovers.
                if (error instanceof ApiError && error.status === HTTP_STATUS.UNAUTHORIZED) {
                    authSession.clearSession();
                } else {
                    toast.error(getFailureMessage(error, 'Could not check your session'));
                }
            } finally {
                if (!isCancelled) {
                    setProbing(false);
                }
            }
        })();
        return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only: a re-run refetches
    }, []);

    // Initialize Google Sign-In + render the button whenever the sign-in screen
    // (re)appears — first load, or back from a later screen.
    const isSignInShown = !isProbing && !needsSetup && orgChoices === null;
    useEffect(() => {
        if (!isSignInShown) {
            return;
        }
        const clientId = cfg.googleClientId;
        if (!clientId) {
            return;
        }
        let isCancelled = false;
        (async () => {
            try {
                if (isPopupFlow) {
                    // Redirect mode round-trips through Google as a top-level
                    // navigation; the return path can't ride the fixed login_uri, so
                    // it goes into a short-lived cookie the endpoint consumes.
                    // SameSite=None because Google delivers the credential via a
                    // cross-site form POST.
                    const attrs = location.protocol === 'https:'
                        ? '; SameSite=None; Secure' : '; SameSite=Lax';
                    document.cookie = 'occurra_login_next=' +
                        encodeURIComponent(nextText) + '; Path=/; Max-Age=600' + attrs;
                }
                await initGoogleSignIn({
                    clientId,
                    onCredential,
                    uxMode: isPopupFlow ? GoogleUxMode.Redirect : undefined,
                    loginUri: location.origin + '/vms/api/v1/auth/google-redirect',
                });
                if (isCancelled) {
                    return;
                }
                if (buttonRef.current) {
                    renderGoogleButton(buttonRef.current);
                }
                promptGoogleSignIn();
            } catch (error) {
                toast.error(getFailureMessage(error, 'Google Sign-In unavailable'));
            }
        })();
        return () => {
            isCancelled = true;
            cancelGooglePrompt();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCredential is new each render
    }, [isSignInShown, cfg.googleClientId]);

    // Every screen of the page sits on the same full-height backdrop.
    const pageClass = cn(
        'theme-occurra flex h-dvh items-center justify-center bg-occ-background-main',
        'text-occ-foreground-main',
    );
    const secondaryButtonClass = cn(
        'rounded border border-occ-border-1 px-3 py-2 text-sm',
        'not-disabled:hover:bg-occ-foreground-25 not-disabled:active:bg-occ-foreground-50',
        'disabled:text-occ-foreground-disabled',
    );
    const createBlockedReason = pendingText ??
        (orgName.trim() ? undefined : 'Enter an organization name');

    // The way out of every screen past the Google button: the account it is
    // signed in as, and signing in with another one instead.
    const account = (
        <div className={cn(
            'flex w-full items-center gap-3 border-t border-occ-border-1 pt-4',
            'text-xs text-occ-foreground-tertiary',
        )}
        >
            <span
                className="min-w-0 flex-1 truncate"
                title={email}
            >
                {email}
            </span>
            <Hint label={pendingText}>
                <button
                    type="button"
                    disabled={isBusy}
                    className={cn(
                        // The padding gives the hover room; the margin keeps the label in line.
                        '-mx-1.5 -my-0.5 shrink-0 rounded px-1.5 py-0.5 font-medium',
                        'not-disabled:hover:bg-occ-foreground-25',
                        'disabled:text-occ-foreground-disabled',
                    )}
                    onClick={onUseAnotherAccount}
                >
                    Use another account
                </button>
            </Hint>
        </div>
    );

    if (orgChoices && !needsSetup) {
        const currentOrgId = canReturn ? authSession.getActiveOrg() : null;
        return (
            <div className={pageClass}>
                <div className="occ-card-surface flex w-full max-w-md flex-col gap-6 p-8">
                    <div className="flex flex-col items-center gap-1 text-center">
                        <img
                            src={occurraLogo}
                            alt="Occurra"
                            className="size-12 rounded-full object-cover"
                        />
                        <h1 className="mt-2 text-xl font-semibold">Choose an organization</h1>
                        <p className="text-sm text-occ-foreground-tertiary">
                            Pick the organization to continue with
                        </p>
                    </div>
                    <ul
                        className="flex flex-col gap-2"
                        aria-busy={isBusy}
                    >
                        {orgChoices.map((choice) => (
                            <OrgChoiceRow
                                key={choice.orgId}
                                choice={choice}
                                isCurrent={!choice.invited && choice.orgId === currentOrgId}
                                disabled={isBusy}
                                disabledReason={pendingText}
                                onPick={onPickOrg}
                                onDecline={onCancelInvite}
                            />
                        ))}
                        <li className="mt-1 border-t border-occ-border-1 pt-3">
                            <Hint label={pendingText}>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    className={cn(
                                        'flex w-full items-center gap-3 rounded-lg',
                                        'border border-dashed border-occ-border-1 px-4 py-3',
                                        'text-left text-occ-foreground-tertiary',
                                        'not-disabled:hover:bg-occ-foreground-25',
                                        'disabled:text-occ-foreground-disabled',
                                    )}
                                    onClick={() => setNeedsSetup(true)}
                                >
                                    <Plus className="size-4 shrink-0" />
                                    <span className="text-sm font-medium">
                                        Create a new organization
                                    </span>
                                </button>
                            </Hint>
                        </li>
                    </ul>
                    {canReturn && (
                        <Hint label={pendingText}>
                            <button
                                type="button"
                                disabled={isBusy}
                                className={secondaryButtonClass}
                                onClick={() => navigate(nextText, { replace: true })}
                            >
                                Cancel
                            </button>
                        </Hint>
                    )}
                    {account}
                </div>
            </div>
        );
    }

    let title = 'Sign in to Occurra';
    if (needsSetup) {
        title = orgChoices ? 'Create a new organization' : 'Create your organization';
    }
    return (
        <div className={pageClass}>
            <div className="occ-card-surface flex w-full max-w-sm flex-col items-center gap-6 p-8">
                <div className="flex flex-col items-center gap-1 text-center">
                    <img
                        src={occurraLogo}
                        alt="Occurra"
                        className="size-12 rounded-full object-cover"
                    />
                    <h1 className="mt-2 text-xl font-semibold">{title}</h1>
                </div>

                {!needsSetup && isProbing && (
                    <p className="text-sm text-occ-foreground-tertiary">Checking session…</p>
                )}
                {!needsSetup && !isProbing && (
                    <>
                        {!cfg.googleClientId && (
                            <p className="text-xs text-occ-red-1000">
                                Google Sign-In is not configured on the server (no
                                <code className="mx-1 rounded bg-occ-foreground-25 px-1">
                                    googleClientId
                                </code>
                                in
                                {' '}
                                <code className="rounded bg-occ-foreground-25 px-1">
                                    /vms/api/v1/config
                                </code>
                                ).
                            </p>
                        )}
                        <div
                            ref={buttonRef}
                            aria-busy={isBusy}
                        />
                    </>
                )}

                {needsSetup && (
                    <>
                        <form
                            className="flex w-full flex-col gap-2"
                            onSubmit={onCreateOrg}
                        >
                            <label
                                htmlFor="orgName"
                                className="text-sm font-medium"
                            >
                                Organization name
                            </label>
                            <Input
                                id="orgName"
                                autoFocus
                                required
                                placeholder="My Company"
                                value={orgName}
                                onChange={(event) => setOrgName(event.target.value)}
                            />
                            <div className="mt-1 flex gap-2">
                                {orgChoices && (
                                    <Hint label={pendingText}>
                                        <button
                                            type="button"
                                            disabled={isBusy}
                                            className={secondaryButtonClass}
                                            onClick={() => setNeedsSetup(false)}
                                        >
                                            Back
                                        </button>
                                    </Hint>
                                )}
                                <Hint label={createBlockedReason}>
                                    <button
                                        type="submit"
                                        disabled={createBlockedReason !== undefined}
                                        className={cn(
                                            'flex-1 rounded px-3 py-2 text-sm font-medium',
                                            'text-occ-foreground-inverted',
                                            'not-disabled:bg-occ-foreground-high-contrast',
                                            // As on the accent Button: the fill is already the
                                            // extreme step, so the hover steps back from it.
                                            'not-disabled:hover:bg-occ-foreground-main',
                                            'disabled:text-occ-foreground-disabled',
                                        )}
                                    >
                                        {isBusy ? 'Creating…' : 'Create and continue'}
                                    </button>
                                </Hint>
                            </div>
                        </form>
                        {account}
                    </>
                )}
            </div>
        </div>
    );
};
